/**
 * 懒加载组件
 *
 * img标签用法：
 * <img src="" class="js-lazyload-el" data-lazyload-src="//cdnimg.zuzuche.com/xxxx.jpg">
 * 加载后：
 * <img src="//cdnimg.zuzuche.com/xxxx.jpg">
 *
 * 背景标签用法：
 * <div class="js-lazyload-el" data-lazyload-src="//cdnimg.zuzuche.com/xxxx.jpg"></div>
 * 加载后：
 * <div style="background-image:url(//cdnimg.zuzuche.com/xxxx.jpg)"></div>
 */

import $ from 'zzc-zepto';
import Events from 'zzc-arale-events';
import { throttle } from 'throttle-debounce';
import {
  getViewportHeight
} from '../viewportSize';
import {
  isSupportHttpsImg,
  ImgProcess
} from './index';
import * as Scroll from '../scroll';
// eslint-disable-next-line
import defaultImgBase64 from '../../component/Icon/svg/zzc-default-img.svg?__inline';

const $window = $(window);

const events = new Events();

let elementQueueData = [];

// 给图片固定加个规则
let fixedImgHandle = null;

function getRandomArbitrary(min, max) {
  return Math.random() * (max - min) + min;
}

function loadImg(url, options = {}) {
  // eslint-disable-next-line
  let node = new Image();

  node.onload = () => {
    options.onload();
    // eslint-disable-next-line
    node.onload = node.onerror = null;
    node = null;
  };

  node.onerror = () => {
    options.onerror();
    // eslint-disable-next-line
    node.onload = node.onerror = null;
    node = null;
  };

  node.src = url;

  if (node.complete) {
    node.onload();
  }
}

const loadRetryMax = 3;


function loadAsset(elementQueueInfo) {
  let elNode = elementQueueInfo.el[0];

  events.trigger('beforeLoad', elementQueueInfo);

  let url = elNode.getAttribute('data-lazyload-src');

  if (url) {
    // 图片cdn转换
    if (ImgProcess.isCDNUrl(url)) {
      let imgHandleOptions = elNode.getAttribute('data-img-handle') || '';
      imgHandleOptions = ImgProcess.parse(imgHandleOptions);
      // 当前有both或者fw fh的规则时 不设定（一般这个是为了设固定宽为750）
      const rules = ['both', 'fh', 'fwfh'];
      if (!rules.some((item) => {
        return imgHandleOptions[item];
      })
        && fixedImgHandle
        && !rules.some((item) => {
          return url.indexOf(item) > -1;
        })
      ) {
        const fixedImgHandleOptions = ImgProcess.parse(fixedImgHandle);
        Object.keys(fixedImgHandleOptions).forEach((item) => {
          imgHandleOptions[item] = fixedImgHandleOptions[item];
        });
      }
      imgHandleOptions.format = 'webp';
      url = ImgProcess.append(url, imgHandleOptions);
    }

    // 强制CDN图片走https
    if (isSupportHttpsImg(url)) {
      url = url.replace('http://', 'https://');
    }

    loadImg(url, {
      onload: () => {
        if (elNode.nodeName === 'IMG') {
          elNode.setAttribute('src', url);
        } else {
          elNode.style.backgroundImage = `url(${url})`;
        }

        elNode.removeAttribute('data-lazyload-src');

        events.trigger('onload', elementQueueInfo);
        // eslint-disable-next-line
        elNode = elementQueueInfo = null;
      },
      onerror: () => {
        // eslint-disable-next-line
        ++elementQueueInfo.errorCount;
        if (elementQueueInfo.errorCount < loadRetryMax) {
          // 1至2秒后重试
          setTimeout(() => {
            loadAsset(elementQueueInfo);
          }, getRandomArbitrary(500, 1500));
        } else {
          // 有一种特殊的情况会导致图片加载失败
          // 即原文件类型是webp但图片显示后缀是.jpg，在不支持webp的浏览器会出现加载失败，则需要强制formar为jpg或png
          elNode.setAttribute('src', ImgProcess.append(url, '/format/jpg'));
          elNode.removeAttribute('data-lazyload-src');
          elNode.classList.add('lazyload-error');
          events.trigger('onerror', elementQueueInfo);

          elementQueueInfo = null;
        }
      }
    });
  } else {
    events.trigger('load', elementQueueInfo);
  }
}

/**
 * 遍历元素检查offset
 */
function checkElement() {
  const len = elementQueueData.length;

  if (!len) {
    return;
  }

  const scrollTop = $window.scrollTop();

  const viewportHeight = getViewportHeight();
  const offsetHeightRange = viewportHeight / 2;

  const newQueueData = [];

  for (let i = 0; i < len; i += 1) {
    const offset = elementQueueData[i].el.offset();
    if (scrollTop - offsetHeightRange < offset.top
            && viewportHeight + scrollTop + offsetHeightRange > offset.top
    ) {
      loadAsset(elementQueueData[i]);
    } else {
      newQueueData.push(elementQueueData[i]);
    }
  }

  elementQueueData = newQueueData;
}


/**
 * 触发元素检查
 */
let triggerEventTimer;

function triggerEvent() {
  if (runState === false) {
    return;
  }

  if (triggerEventTimer) {
    clearTimeout(triggerEventTimer);
    triggerEventTimer = null;
  }

  triggerEventTimer = setTimeout(() => {
    triggerEventTimer = null;

    checkElement();
  }, 100);
}

/**
 * 延迟加载的元素收集
 */
export function collect() {
  const $collectElement = $('.js-lazyload-el');

  if ($collectElement.length < 1) {
    return;
  }

  for (let i = 0, len = $collectElement.length; i < len; i += 1) {
    const $el = $collectElement.eq(i);
    elementQueueData.push({
      el: $el,
      errorCount: 0
    });

    $el.removeClass('js-lazyload-el');
  }
}

/**
 * 清除懒加载队列
 */
export function clean() {
  events.off();
  elementQueueData = [];
}

/**
 * 元素加载
 */
export function on(type, callback, context) {
  return events.on(type, callback, context);
}
export function once(type, callback, context) {
  return events.once(type, callback, context);
}


/**
 * 手动触发检查
 */
export function triggerCheck() {
  checkElement();
}

let runState = true;
/**
 * 开始检查元素
 */
export function play() {
  runState = true;
}
/**
 * 暂停检查元素
 */
export function pause() {
  runState = false;
}

let windowEventBind = false;

export function init() {
  if (!windowEventBind) {
    Scroll.bind('window', (response) => {
      triggerEvent(response.event);
    });
    $window.on('resize', () => {
      triggerEvent();
    });
    windowEventBind = true;
  }

  collect();

  // 入页面时触发一下
  setTimeout(() => {
    triggerEvent();
  }, 0);
}


export function customScrollInit(selector = window, imgHandle = null) {
  fixedImgHandle = imgHandle;
  $(selector).on('scroll', throttle(300, () => {
    triggerEvent();
  }));
  collect();
  // 入页面时触发一下
  setTimeout(() => {
    triggerEvent();
  }, 0);
}
